import React, { useContext, useEffect } from "react";

import { PollProps } from "@/containers/Poll/PollContainer";
import { getPollOptionVotesPercent, PollContext } from "@/contexts";
import { Option, PollVote } from "@/service/Model";

import { OptionButton, OptionResult } from "./components/Option";
import * as Styled from "./styles";

export enum Animation {
  FADE_OUT,
  FADE_IN,
}
export const FADE_DURATION = 300;
export const ANIMATION_DURATION = 450;
export const RESTING_BEFORE_FADE_IN_RESULTS = 100;
export const RESTING_BEFORE_ANIMATE_PROGRESSBAR = 50;

const LightPoll: React.FC<PollProps> = (props: PollProps): JSX.Element => {
  const { interaction, interactionResults, votes } = props;
  const { participantsCount, results } = interactionResults;
  const pollContext = useContext(PollContext);

  const slide = interaction.slides[0];
  const options = slide.options;
  const voted = votes.length > 0;
  const participantsCountPlusVote = voted
    ? participantsCount + 1
    : participantsCount;

  const [participated] = React.useState<boolean>(voted);
  const [animation, setAnimation] = React.useState<Animation | undefined>();

  useEffect(() => {
    if (animation === Animation.FADE_OUT) {
      const timerId = window.setTimeout(() => {
        setAnimation(Animation.FADE_IN);
      }, FADE_DURATION + RESTING_BEFORE_FADE_IN_RESULTS);
      return () => window.clearTimeout(timerId);
    }
  }, [animation]);

  const getPercent = (optionId: string) => {
    const isUserAnswer = !!votes.find(
      (vote: PollVote) => vote.optionId === optionId
    );
    return getPollOptionVotesPercent(results, optionId, true, isUserAnswer);
  };

  const showResults =
    participated || (animation === Animation.FADE_IN && voted);

  const renderOption = (option: Option): JSX.Element =>
    showResults ? (
      <OptionResult
        key={`result-${option.id}`}
        animationDuration={ANIMATION_DURATION}
        animationDelay={
          participated
            ? RESTING_BEFORE_ANIMATE_PROGRESSBAR
            : RESTING_BEFORE_ANIMATE_PROGRESSBAR + FADE_DURATION
        }
        text={option.content}
        percent={getPercent(option.id)}
      />
    ) : (
      <OptionButton
        key={`button-${option.id}`}
        text={option.content}
        onClick={() => {
          if (voted) {
            return;
          }
          pollContext.onVote({
            interactionId: interaction.id,
            optionId: option.id,
            slideId: slide.id,
          });
          setAnimation(Animation.FADE_OUT);
        }}
      />
    );

  return (
    <Styled.Center>
      <Styled.MainWrapper>
        <header>
          {slide.topline && <Styled.Topline>{slide.topline}</Styled.Topline>}
          <Styled.Headline>{slide.title}</Styled.Headline>
        </header>
        <Styled.OptionsWrapper animation={animation}>
          {options.map(renderOption)}
        </Styled.OptionsWrapper>
        <Styled.Footer>
          <Styled.Participant>
            {participantsCountPlusVote.toLocaleString()}
          </Styled.Participant>
          abgegebene Stimmen
        </Styled.Footer>
      </Styled.MainWrapper>
    </Styled.Center>
  );
};

export default LightPoll;
